import {isValidEmailAddress} from '@cip/core/utils';
import {onAuthStateChanged, getAuth} from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  getFirestore,
  serverTimestamp,
  setDoc,
  type FieldValue,
} from 'firebase/firestore';
import {useEffect, useState} from 'react';
import styled from 'styled-components';
import type {RoleFirebaseString} from 'types/role';

/**
 * Roles a visitor can express interest in for Code in Place X, in display
 * order. A subset of the app's canonical roles, validated against
 * {@link RoleFirebaseString} so a renamed role fails to compile here.
 */
const INTERESTED_ROLE_IDS = [
  'student',
  'sl',
] as const satisfies readonly RoleFirebaseString[];

/** Id of a role a visitor can express interest in. */
type InterestedRoleId = (typeof INTERESTED_ROLE_IDS)[number];

/** Human-readable label shown in the form for each interest role. */
const INTERESTED_ROLE_LABELS: Record<InterestedRoleId, string> = {
  student: 'Student',
  sl: 'Section Leader',
};

/**
 * Shape of a document written to `forms/<campaignCollection>/responses`. Local
 * to this client-only form (no reader/other consumer), so it isn't promoted to
 * `@cip/core/firebase`; graduate it there if a function or dashboard ever reads
 * these responses.
 */
interface CipxInterestResponse {
  /** Email the visitor wants to be contacted at. */
  correctEmail: string;
  /** Role the visitor is interested in joining as. */
  interestedRole: InterestedRoleId;
  /** Firebase uid if signed in, else '' for anonymous visitors. */
  userId: string;
  /** Server-set submission time; always `serverTimestamp()` on write. */
  timestamp: FieldValue;
  /**
   * Optional campaign discriminator (e.g. 'pai') written so that several
   * campaigns can safely share one open-write collection and still be
   * filtered apart by readers. Omitted from the document when not provided.
   */
  campaign?: string;
}

/**
 * Props for {@link CipxInterestForm}. All optional; the defaults reproduce the
 * original Code in Place X behavior (writes to `forms/cipxInterest/responses`,
 * no campaign tag, "Code in Place X" confirmation copy).
 */
interface CipxInterestFormProps {
  /**
   * Middle path segment of the responses collection: responses are written to
   * `forms/<campaignCollection>/responses`. Must be a path already permitted by
   * Firestore rules. Defaults to 'cipxInterest'.
   */
  campaignCollection?: string;
  /**
   * Optional campaign discriminator written as the `campaign` field on each
   * response, used to filter one campaign's leads out of a shared collection.
   * When omitted, no `campaign` field is written.
   */
  campaign?: string;
  /**
   * Campaign name shown in the confirmation copy ("Thanks for your interest in
   * &lt;name&gt;"). Defaults to 'Code in Place X'.
   */
  campaignName?: string;
  /**
   * Viewport width in pixels at or below which the form renders in its compact
   * ("mobile") style: card chrome dropped and the submit button left-aligned.
   * The compact range is the closed interval `[0, collapseBreakpointPx]`. Hosts
   * should pass the same breakpoint at which their page collapses to a single
   * column. Defaults to 768.
   */
  collapseBreakpointPx?: number;
}

/**
 * Embeddable express-interest form for an upcoming course/campaign. Collects an
 * email and whether the visitor is interested in joining as a student or as a
 * section leader, then writes the response to
 * `forms/<campaignCollection>/responses` in Firestore.
 *
 * Defaults reproduce the original Code in Place X behavior (writes to the
 * `cipxInterest` collection). Other campaigns can reuse an already open-write
 * collection (e.g. `generalInterest`) and pass a {@link CipxInterestFormProps.campaign}
 * tag so their leads are filterable apart from other campaigns sharing that
 * collection — this avoids needing a new Firestore rule per campaign.
 *
 * Rendered as a self-contained, modern card (no Bootstrap) so it can be dropped
 * into a landing page. At and below the host's collapse breakpoint (see
 * {@link CipxInterestFormProps.collapseBreakpointPx}) the card chrome is
 * dropped and the form content renders bare, letting the host page's
 * background show through.
 *
 * @param props - Campaign configuration; see {@link CipxInterestFormProps}. All
 *   fields optional — omitting `props` entirely yields the CIPX defaults.
 * @return The rendered interest form card, or a confirmation card after the
 *   response is submitted.
 */
export const CipxInterestForm = ({
  campaignCollection = 'cipxInterest',
  campaign,
  campaignName = 'Code in Place X',
  collapseBreakpointPx = 768,
}: CipxInterestFormProps = {}) => {
  const db = getFirestore();
  const interestsColRef = collection(
    db,
    'forms',
    campaignCollection,
    'responses',
  );
  const [responseDocId, setResponseDocId] = useState<string | undefined>(
    undefined,
  );
  const [editingMode, setEditingMode] = useState(true);
  const [email, setEmail] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [interestedRole, setInterestedRole] =
    useState<InterestedRoleId>('student');
  // Inline submit/validation error shown inside the card, next to the button
  // the visitor just clicked. Empty string means no error.
  const [errorMessage, setErrorMessage] = useState('');
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      if (user) {
        // User is signed in; pre-fill the (still-visible, editable) email field
        // with their account email unless they've already typed something.
        // Some providers (e.g. anonymous auth) have a null email; coerce to ''
        // so `authEmail` stays a string and the submit-time validation below
        // always receives a string.
        setAuthEmail(user.email ?? '');
        setEmail(prev => (prev ? prev : (user.email ?? '')));
      }
    });

    // Cleanup subscription on unmount.
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auth instance is stable for the lifetime of the component
  }, []);

  const onSubmit = async () => {
    setErrorMessage('');
    const userId = auth.currentUser ? auth.currentUser.uid : '';
    // The visible email field is the source of truth; fall back to the account
    // email only if it's somehow empty.
    const correctEmail = email ? email : authEmail;
    if (!isValidEmailAddress(correctEmail)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    const responsePayload: CipxInterestResponse = {
      correctEmail,
      interestedRole,
      userId,
      timestamp: serverTimestamp(),
      // Only tag the doc when a campaign is set, so shared collections (e.g.
      // generalInterest) can be filtered per campaign without polluting
      // untagged CIPX responses with an empty field.
      ...(campaign ? {campaign} : {}),
    };

    try {
      if (!responseDocId) {
        const response = await addDoc(interestsColRef, responsePayload);
        setResponseDocId(response.id);
      } else {
        // merge so a re-submit updates the existing response rather than
        // destructively replacing it.
        await setDoc(doc(interestsColRef, responseDocId), responsePayload, {
          merge: true,
        });
      }
      setEditingMode(false);
    } catch (e) {
      // Surface the failure instead of leaving the click looking like a no-op:
      // without this, a rejected write (offline, rules, quota) skips
      // setEditingMode(false) and the visitor sees neither confirmation nor error.
      console.error('[CipxInterest] Failed to save interest response', e);
      setErrorMessage(
        'Something went wrong saving your response. Please try again.',
      );
    }
  };

  if (!editingMode) {
    return (
      <Card $collapseAt={collapseBreakpointPx}>
        <Header>
          <Heading>You're on the list</Heading>
          <Subheading>
            Thanks for your interest in {campaignName}
            {email ? `, ${email}` : ''}. We'll be in touch as a{' '}
            <strong>{INTERESTED_ROLE_LABELS[interestedRole]}</strong>.
          </Subheading>
        </Header>
        <SecondaryButton onClick={() => setEditingMode(true)}>
          Edit response
        </SecondaryButton>
      </Card>
    );
  }

  return (
    <Card $collapseAt={collapseBreakpointPx}>
      <Header>
        <Eyebrow>Applications open soon</Eyebrow>
        <Heading>Get notified!</Heading>
        <Subheading>
          Share your email and let us know how you'd like to
          participate.
        </Subheading>
      </Header>

      <Field>
        <FieldLabel htmlFor="cipx-email">Email</FieldLabel>
        <Input
          type="email"
          id="cipx-email"
          name="email"
          placeholder="you@example.com"
          autoComplete="email"
          value={email}
          onChange={e => {
            setEmail(e.target.value);
            setErrorMessage('');
          }}
        />
      </Field>

      <Field as="fieldset">
        <FieldLabel as="legend">I'm interested in being a</FieldLabel>
        <RadioGroup>
          {INTERESTED_ROLE_IDS.map(id => (
            <RadioOption key={id}>
              <input
                type="radio"
                name="cipx-role"
                value={id}
                checked={interestedRole === id}
                onChange={() => setInterestedRole(id)}
              />
              <span>{INTERESTED_ROLE_LABELS[id]}</span>
            </RadioOption>
          ))}
        </RadioGroup>
      </Field>

      {errorMessage && <ErrorText role="alert">{errorMessage}</ErrorText>}

      <PrimaryButton $collapseAt={collapseBreakpointPx} onClick={onSubmit}>
        Submit
      </PrimaryButton>
    </Card>
  );
};

const Card = styled.div<{$collapseAt: number}>`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  max-width: 440px;
  padding: 2.5rem;
  background-color: #fff;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  color: #1d1d1f;

  /* At the host page's collapse breakpoint, drop the card chrome (padding,
     shadow, rounding) and show the bare form content — a padded card wastes
     too much of a narrow screen. The width cap goes too so text only wraps
     when the viewport requires it; the input keeps its own cap below. */
  @media (max-width: ${p => p.$collapseAt}px) {
    padding: 0;
    background-color: transparent;
    border-radius: 0;
    box-shadow: none;
    max-width: none;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Eyebrow = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8c1515;
`;

const Heading = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0;
`;

const Subheading = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: #6e6e73;
  margin: 0;
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: #c0392b;
  text-align: center;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  /* reset default fieldset chrome when rendered as a <fieldset> */
  border: none;
  margin: 0;
  padding: 0;
  min-inline-size: 0;
`;

const FieldLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1d1d1f;
`;

const Input = styled.input`
  width: 100%;
  /* Matches the card's default width so the field doesn't stretch across the
     whole viewport when the compact rendering removes the card's cap. */
  max-width: 440px;
  padding: 0.85rem 1rem;
  font-family: inherit;
  font-size: 1rem;
  border: 1px solid #d2d2d7;
  border-radius: 12px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: #8c1515;
    box-shadow: 0 0 0 4px rgba(140, 21, 21, 0.12);
  }

  &::placeholder {
    color: #a1a1a6;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-size: 1rem;
  color: #1d1d1f;
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    accent-color: #8c1515;
    cursor: pointer;
  }
`;

const PrimaryButton = styled.button<{$collapseAt: number}>`
  align-self: center;

  /* In the compact (bare, left-aligned) rendering the button follows suit. */
  @media (max-width: ${p => p.$collapseAt}px) {
    align-self: flex-start;
  }
  border: none;
  background-color: #8c1515;
  color: #fff;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.6rem 1.4rem;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.12s ease, background-color 0.15s ease;

  &:hover {
    background-color: #6f1010;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SecondaryButton = styled.button`
  align-self: flex-start;
  border: 1px solid #d2d2d7;
  background-color: #fff;
  color: #1d1d1f;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.6rem 1.25rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f5f5f7;
  }
`;
